import { computed, inject } from "@angular/core";
import { Notification, NotificationControllerGetManyRequestParams, NotificationService, type QueryOrder } from "@api/pushnotification";
import { patchState, signalStore, type, withMethods, withProps, withComputed } from "@ngrx/signals";
import { setAllEntities, withEntities } from "@ngrx/signals/entities";
import { withLoading } from "../features/loading.feature";
import { PaginationStoreState, withPagination } from "../features/pagination.feature";
import { SortState, withSort } from "../features/sort.feature";
import { pipe, switchMap, tap } from "rxjs";
import { StorageKeys, StoreCollection } from "src/app/enums";
import { SearchState, withSearch } from "../features/search.feature";
import { ColumnsState, withColumns } from "../features/columns.feature";
import { withDevtools, withStorageSync } from "@angular-architects/ngrx-toolkit";
import { environment } from "src/environments/environment";
import { tapResponse } from "@ngrx/operators";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import * as Sentry from "@sentry/angular";
import { DEFAULT_PAGINATION_SIZE } from "src/app/constants";
import { selectedProgramId } from "src/app/utils/selected-program-id";

const paginationInitialState: PaginationStoreState<StoreCollection.Notifications> = {
  paginations: {
    [StoreCollection.Notifications]: { pageSize: DEFAULT_PAGINATION_SIZE, pageCount: 0, page: 1, total: 0 },
  },
};

const searchInitialState: SearchState<StoreCollection.Notifications> = {
  searchTerms: {
    [StoreCollection.Notifications]: "",
  },
};

const sortInitialState: SortState<StoreCollection.Notifications> = {
  sorts: {
    [StoreCollection.Notifications]: { active: "sendAt", direction: "desc" },
  },
};

const columnsInitialState: ColumnsState<StoreCollection.Notifications> = {
  columns: {
    [StoreCollection.Notifications]: [
      "message",
      "resourceType",
      "targetType",
      "devices",
      "sendAt",
      "duration",
      "pushOpened",
      "status",
    ] satisfies (keyof Notification | "duration")[],
  },
};

export const PushNotificationStore = signalStore(
  { providedIn: "root" },
  withEntities({ entity: type<Notification>(), collection: StoreCollection.Notifications }),
  withLoading(),
  withPagination(paginationInitialState),
  withSearch(searchInitialState),
  withSort(sortInitialState),
  withColumns(columnsInitialState),
  withProps(() => ({
    notificationService: inject(NotificationService),
  })),
  withComputed(store => ({
    query: computed<NotificationControllerGetManyRequestParams>(() => ({
      limit: store.paginations[StoreCollection.Notifications].pageSize(),
      order: store.sorts[StoreCollection.Notifications].direction() as QueryOrder,
      orderBy: store.sorts[StoreCollection.Notifications].active(),
      page: store.paginations[StoreCollection.Notifications].page(),
      searchTerm: store.searchTerms[StoreCollection.Notifications](),
    })),
  })),
  withMethods(store => ({
    getNotifications: rxMethod<ReturnType<typeof store.query>>(
      pipe(
        tap(() => store.setLoading(true)),
        switchMap(params =>
          store.notificationService.notificationControllerGetMany(params).pipe(
            tapResponse({
              next: notifications => {
                patchState(store, setAllEntities(notifications.data, { collection: StoreCollection.Notifications }));
                store.setPagination({ total: notifications.total }, StoreCollection.Notifications);
              },
              error: err => {
                Sentry.captureException(err);
              },
              finalize: () => store.setLoading(false),
            }),
          ),
        ),
      ),
    ),
  })),
  withDevtools(StorageKeys.PushNotificationStore),
  withStorageSync({
    key: `${selectedProgramId}_selfservice_${environment.name}_${StorageKeys.PushNotificationStore}`,
    select: state => ({
      notificationsEntityMap: state.notificationsEntityMap,
      notificationsIds: state.notificationsIds,
      columns: state.columns,
      sorts: state.sorts,
      paginations: state.paginations,
    }),
  }),
);
